/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-02-01 21:40:54
 */

interface DownloadConfig {
  path: string
  openFolderWhenDone: boolean
}

/** 本地写入失败的原因码，桌面端 1.3.5 起下发 */
export type LocalWriteErrorReasonCode =
  | 'DISK_FULL'
  | 'DIR_NOT_FOUND'
  | 'LOCAL_IO_UNKNOWN'

/** 桌面端因本地写入失败中止下载任务时下发的通知数据 */
export interface DownloadLocalWriteErrorData {
  /** 运行时任务 id，与下载记录列表里的 task.id 一致 */
  taskId: string
  /** 数据库任务 id */
  dbId: number
  reasonCode: LocalWriteErrorReasonCode
  /** 桌面端给的中文文案，可直接展示 */
  error: string
  /** 触发时的连续失败张数，桌面端 1.3.5 固定为 5 */
  failureCount: number
  /** 桌面端已把任务置为暂停 */
  status: 'paused'
}

/** 客户端打开外部链接的结果，失败时 error 是中文原因 */
export interface OpenExternalResult {
  success: boolean
  error?: string
}

/**
 * AI 电脑版（TodayWork）安装包的后台预下载状态，客户端 1.3.5 起提供。
 *
 * 客户端启动 10 秒后会把安装包下到自己的数据目录，下完推 ready，
 * 用户点安装时装包已经在本地，不用等下载。
 */
export type InstallerStatus =
  | { state: 'idle' }
  | { state: 'checking' }
  | {
      state: 'downloading'
      /** 0-100 的整数 */
      progress: number
      receivedBytes: number
      totalBytes: number
    }
  | {
      state: 'ready'
      /** 安装包版本号，如 1.260828.2 */
      version: string
      /** 安装包文件名，如 TodayWork-1.260828.2-x64.exe */
      filename: string
    }
  | { state: 'failed'; error: string }

/** 运行安装包的结果，success 只表示已交给操作系统，装没装成客户端也不知道 */
export interface InstallerRunResult {
  success: boolean
  error?: string
}

/**
 * AI 电脑版（TodayWork）在本机的安装状态，客户端 1.3.6 起提供。
 *
 * 客户端约定这个查询永远 resolve、不 reject，查不出来时按未安装返回。
 */
export interface AiClientStatus {
  installed: boolean
  /** 已安装时的应用显示名，仅供展示 */
  appName?: string
}

interface DesktopLib {
  isDesktop: boolean
  /** 客户端是否支持 openExternal 桥（1.3.5 起提供） */
  supportsOpenExternal: boolean
  /** 客户端是否支持安装包预下载（1.3.5 起提供），用特性检测而不是版本号判断 */
  supportsInstaller: boolean
  /**
   * 客户端是否能查 AI 电脑版装没装（1.3.6 起提供）。
   * 1.3.5 有 openExternal 但没有这个查询，两者要分开判断
   */
  supportsAiClientStatus: boolean
  loginSuccess: () => void
  logoutSuccess: () => void
  openContact: (contactUrl: string) => void
  /** 把链接交给操作系统打开，只放行 http / https / todaywork 协议 */
  openExternal: (url: string) => Promise<OpenExternalResult>

  // AI 电脑版安装包预下载
  /** 订阅状态变化，全应用只订阅一次（见 useDesktopInstaller） */
  onInstallerStatus: (
    callback: (event: unknown, status: InstallerStatus) => void
  ) => void
  /** 取消订阅，内部是 removeAllListeners，会清掉该通道的所有监听 */
  offInstallerStatus: () => void
  /** 主动查一次当前状态 */
  installerGetStatus: () => Promise<InstallerStatus>
  /** 运行已下载好的安装包 */
  installerRun: () => Promise<InstallerRunResult>
  /** 查 AI 电脑版是否已安装，调用前先判断 supportsAiClientStatus */
  aiClientGetStatus: () => Promise<AiClientStatus>

  getDownloadConfig: () => Promise<DownloadConfig>
  chooseDownloadPath: () => Promise<string>
  updateDownloadConfig: (config: DownloadConfig) => Promise<string>
  clientInfo?: any
  uploadLogs: (params: any) => Promise<any>

  // 下载相关方法
  downloadCreateTask: (params: any) => Promise<any>
  downloadGetTasks: (groupID: string) => Promise<any>
  downloadPauseTask: (taskId: string) => Promise<any>
  downloadResumeTask: (taskId: string) => Promise<any>
  downloadCancelTask: (taskId: string, deleteFiles?: boolean) => Promise<any>
  downloadRedownloadTask: (taskId: string) => Promise<any>
  openFolder: (path: string) => Promise<any>

  // 下载事件监听
  onDownloadProgressUpdate: (callback: (event: any, data: any) => void) => void
  onDownloadCompleted: (callback: (event: any, data: any) => void) => void
  onDownloadNetworkError: (callback: (event: any, data: any) => void) => void
  onDownloadError: (callback: (event: any, data: any) => void) => void
  onDownloadProgress: (callback: (event: any, data: any) => void) => void
  onDownloadLocalWriteError: (
    callback: (event: unknown, data: DownloadLocalWriteErrorData) => void
  ) => void

  // 清理监听器
  removeDownloadListeners: (channel: string) => void
}

const desktopLib: DesktopLib = {
  isDesktop: !!(window as any).todayCameraAPI,
  supportsOpenExternal:
    typeof (window as any)?.todayCameraAPI?.openExternal === 'function',
  supportsInstaller:
    typeof (window as any)?.todayCameraAPI?.installerGetStatus === 'function',
  supportsAiClientStatus:
    typeof (window as any)?.todayCameraAPI?.aiClientGetStatus === 'function',
  loginSuccess: () => (window as any)?.todayCameraAPI?.loginSuccess(),
  logoutSuccess: () => (window as any)?.todayCameraAPI?.logoutSuccess(),
  openContact: (contactUrl) =>
    (window as any).todayCameraAPI.openContact(contactUrl),
  openExternal: (url) => (window as any)?.todayCameraAPI?.openExternal?.(url),

  // AI 电脑版安装包预下载实现
  onInstallerStatus: (callback) =>
    (window as any)?.todayCameraAPI?.onInstallerStatus?.(callback),
  offInstallerStatus: () =>
    (window as any)?.todayCameraAPI?.offInstallerStatus?.(),
  installerGetStatus: () =>
    (window as any)?.todayCameraAPI?.installerGetStatus?.(),
  installerRun: () => (window as any)?.todayCameraAPI?.installerRun?.(),
  aiClientGetStatus: () =>
    (window as any)?.todayCameraAPI?.aiClientGetStatus?.(),

  getDownloadConfig: () => (window as any)?.todayCameraAPI.getDownloadConfig(),
  chooseDownloadPath: () =>
    (window as any)?.todayCameraAPI.chooseDownloadPath(),
  updateDownloadConfig: (config) =>
    (window as any)?.todayCameraAPI.updateDownloadConfig(config),
  clientInfo: (window as any)?.clientInfo,
  uploadLogs: (params) =>
    (window as any)?.todayCameraAPI?.['upload-logs']?.(params),

  // 下载相关方法实现
  downloadCreateTask: (params) =>
    (window as any)?.todayCameraAPI?.['download:create-task']?.(params),
  downloadGetTasks: (groupID) =>
    (window as any)?.todayCameraAPI?.['download:get-tasks']?.(groupID),
  downloadPauseTask: (taskId) =>
    (window as any)?.todayCameraAPI?.['download:pause-task']?.(taskId),
  downloadResumeTask: (taskId) =>
    (window as any)?.todayCameraAPI?.['download:resume-task']?.(taskId),
  downloadCancelTask: (taskId, deleteFiles) =>
    (window as any)?.todayCameraAPI?.['download:cancel-task']?.(
      taskId,
      deleteFiles
    ),
  downloadRedownloadTask: (taskId) =>
    (window as any)?.todayCameraAPI?.['download:redownload-task']?.(taskId),
  openFolder: (path) =>
    (window as any)?.todayCameraAPI?.['open-folder']?.(path),

  // 下载事件监听实现
  onDownloadProgressUpdate: (callback) =>
    (window as any)?.todayCameraAPI?.['download:progress-update']?.(callback),
  onDownloadCompleted: (callback) =>
    (window as any)?.todayCameraAPI?.['download:completed']?.(callback),
  onDownloadNetworkError: (callback) =>
    (window as any)?.todayCameraAPI?.['download:network-error']?.(callback),
  onDownloadError: (callback) =>
    (window as any)?.todayCameraAPI?.['download:error']?.(callback),
  onDownloadProgress: (callback) =>
    (window as any)?.todayCameraAPI?.['download:progress']?.(callback),
  // 1.3.4 及更早的客户端没有这个通道，可选调用会直接跳过，不会报错
  onDownloadLocalWriteError: (callback) =>
    (window as any)?.todayCameraAPI?.['download:local-write-error']?.(callback),

  // 清理监听器
  removeDownloadListeners: (channel) =>
    (window as any)?.todayCameraAPI?.removeListener?.(channel)
}

export default desktopLib
